import React from 'react';
import { TestTube, CheckCircle, AlertCircle, Eye, EyeOff, RefreshCw, Database } from 'lucide-react';
import type { StorageConfig, TestResults, ShowPasswords } from '../types';

interface StorageSettingsTabProps {
  config: StorageConfig;
  onChange: (config: StorageConfig) => void;
  testResults: TestResults;
  onTest: () => void;
  onReload: () => void;
  showPasswords: ShowPasswords;
  onTogglePassword: (field: string) => void;
}

const StorageSettingsTab: React.FC<StorageSettingsTabProps> = ({
  config,
  onChange,
  testResults,
  onTest,
  onReload,
  showPasswords,
  onTogglePassword
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Configuración de Almacenamiento</h3>
          <p className="text-sm text-gray-600">
            Configura el almacenamiento externo para backups automáticos de prompts de IA y otros datos críticos.
          </p>
        </div>
        {config.isConfigured && (
          <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-green-100 text-green-800">
            <Database className="w-4 h-4" />
            <span className="text-sm font-medium">Guardado en BD</span>
          </div>
        )}
      </div>

      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Servicio</label>
            <select
              value={config.serviceType}
              onChange={(e) => onChange({ ...config, serviceType: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="wasabi">Wasabi</option>
              <option value="s3">Amazon S3</option>
              <option value="supabase">Supabase Storage</option>
              <option value="local">Local Storage</option>
            </select>
          </div>

          {config.serviceType === 'wasabi' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
                <input
                  type="text"
                  value={config.endpoint}
                  onChange={(e) => onChange({ ...config, endpoint: e.target.value })}
                  placeholder="https://s3.wasabisys.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Región</label>
                <input
                  type="text"
                  value={config.region}
                  onChange={(e) => onChange({ ...config, region: e.target.value })}
                  placeholder="us-east-1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Key</label>
                <input
                  type="text"
                  value={config.accessKey}
                  onChange={(e) => onChange({ ...config, accessKey: e.target.value })}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
                <div className="relative">
                  <input
                    type={showPasswords['storageSecretKey'] ? 'text' : 'password'}
                    value={config.secretKey}
                    onChange={(e) => onChange({ ...config, secretKey: e.target.value })}
                    placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => onTogglePassword('storageSecretKey')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords['storageSecretKey'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bucket Name</label>
                <input
                  type="text"
                  value={config.bucketName}
                  onChange={(e) => onChange({ ...config, bucketName: e.target.value })}
                  placeholder="crm-backups"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia de Backup</label>
                <select
                  value={config.backupFrequency}
                  onChange={(e) => onChange({ ...config, backupFrequency: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensual</option>
                </select>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onTest}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <TestTube className="w-4 h-4" />
            <span>Probar Conexión</span>
          </button>

          <button
            onClick={onReload}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Recargar desde BD</span>
          </button>

          {testResults.storage !== undefined && (
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              testResults.storage ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {testResults.storage ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Conexión exitosa</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4" />
                  <span>Error de conexión</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">¿Cómo configurar Wasabi?</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Crea una cuenta en <a href="https://wasabi.com" target="_blank" rel="noopener noreferrer" className="underline">wasabi.com</a></li>
          <li>Ve a "Access Keys" en tu panel de control</li>
          <li>Crea un nuevo Access Key y guarda el Access Key y Secret Key</li>
          <li>Crea un nuevo bucket en la región deseada</li>
          <li>Usa el endpoint correspondiente a tu región (ej: s3.wasabisys.com para us-east-1)</li>
          <li>Los backups se ejecutarán automáticamente según la frecuencia configurada</li>
        </ol>
      </div>

      {config.lastBackup && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Último Backup</h4>
          <p className="text-sm text-gray-600">
            {new Date(config.lastBackup).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default StorageSettingsTab;
